/**
 * Chat Layout Component
 *
 * Main layout for AI Chat with sidebar and chat panel.
 * Provides a ChatGPT-like experience with conversation history.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ChatSidebar, { ChatConversation } from './chat-sidebar';
import ChatPanel from './chat-panel';
import SetupModal from './setup-modal';
import { useAIChat, useDeleteConversation, useRenameConversation } from '../../../hooks/use-ai-chat';
import { useUpdateConversation, useRenameFolder } from '../../../hooks/ai-chat-queries';

interface ChatLayoutProps {
  embedded?: boolean;
  className?: string;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({ embedded = false, className }) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    conversations,
    currentConversationId,
    startNewChat,
    selectConversation,
    clearHistory,
    restartConversation,
  } = useAIChat();
  const deleteConversationMutation = useDeleteConversation();
  const renameConversationMutation = useRenameConversation();
  const updateConversationMutation = useUpdateConversation();
  const renameFolderMutation = useRenameFolder();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobile);

  // Convert API conversations to sidebar format
  const sidebarConversations: ChatConversation[] = React.useMemo(() => {
    return conversations.map((conv) => ({
      id: conv.id,
      title: conv.title || 'New conversation',
      updatedAt: new Date(conv.updatedAt),
      isActive: conv.id === currentConversationId,
      folder: conv.folder ?? null,
      isArchived: conv.isArchived ?? false,
    }));
  }, [conversations, currentConversationId]);

  // NOTE: no eager conversation creation here. Conversations are created
  // lazily by the backend on the first message; showing the welcome screen
  // requires no server-side conversation. Eager creation used to fill the
  // sidebar with empty "New conversation" entries.

  const handleNewChat = useCallback(() => {
    startNewChat();
  }, [startNewChat]);

  const handleSelectConversation = useCallback((id: string) => {
    selectConversation(id);
  }, [selectConversation]);

  const handleRenameConversation = useCallback(
    async (id: string, newTitle: string) => {
      await renameConversationMutation.mutateAsync({ conversationId: id, title: newTitle });
    },
    [renameConversationMutation]
  );

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      await deleteConversationMutation.mutateAsync(id);
      // If we deleted the active conversation, switch to another or create new
      if (id === currentConversationId) {
        const remaining = conversations.filter((c) => c.id !== id);
        if (remaining.length > 0) {
          selectConversation(remaining[0].id);
        } else {
          startNewChat();
        }
      }
    },
    [deleteConversationMutation, currentConversationId, conversations, selectConversation, startNewChat]
  );

  const handleMoveToFolder = useCallback(
    async (id: string, folder: string | null) => {
      await updateConversationMutation.mutateAsync({ conversationId: id, fields: { folder } });
    },
    [updateConversationMutation]
  );

  const handleArchiveConversation = useCallback(
    async (id: string, archived: boolean) => {
      await updateConversationMutation.mutateAsync({ conversationId: id, fields: { isArchived: archived } });
    },
    [updateConversationMutation]
  );

  const handleRenameFolder = useCallback(
    async (from: string, to: string) => {
      await renameFolderMutation.mutateAsync({ from, to });
    },
    [renameFolderMutation]
  );

  const handleClearHistory = useCallback(async () => {
    await clearHistory();
  }, [clearHistory]);

  const handleRestartConversation = useCallback(async () => {
    await restartConversation();
  }, [restartConversation]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  // On mobile, auto-collapse sidebar
  useEffect(() => {
    if (isMobile && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [isMobile]);

  return (
    <Box className={cx(classes.layout, embedded && 'embedded', className)}>
      {/* Sidebar */}
      <ChatSidebar
        conversations={sidebarConversations}
        activeConversationId={currentConversationId || undefined}
        onNewChat={handleNewChat}
        onSelectConversation={handleSelectConversation}
        onRenameConversation={handleRenameConversation}
        onDeleteConversation={handleDeleteConversation}
        onMoveToFolder={handleMoveToFolder}
        onArchiveConversation={handleArchiveConversation}
        onRenameFolder={handleRenameFolder}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />

      {/* Chat in voller Breite — die Einrichtung lebt im Pflichtangaben-Modal */}
      <Box className={classes.chatContainer}>
        <ChatPanel
          embedded
          onClearHistory={handleClearHistory}
          onRestartConversation={handleRestartConversation}
        />
      </Box>

      {!isMobile && <SetupModal />}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  layout: {
    display: 'flex',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    borderRadius: 16,
    boxShadow: theme.shadows[8],
    '&.embedded': {
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  chatContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    overflow: 'hidden',
    minWidth: 0,
    backgroundColor: theme.palette.background.default,
  },
}));

export default ChatLayout;

